import React from "react";
import { Container, Row } from "react-bootstrap";
import { useGoogleSheetApi } from "../hooks/useGoogleSheetApi";
import { googleapi_dailyTrackerDataToJson } from "../util/googleapi_daily_tracker_util";
import {
  DailyTrackerMemberUsageTable,
  IDailyTrackerMemberUsageTable,
} from "../components/DailyTrackerMemberUsage/DailyTrackerMemberUsageTable.component";
import pMap from "p-map";
import {
  carwashApi_getMembership,
  carwashApi_getTransactions,
  IReturnGetTransaction,
} from "../carwashApi";
import { DateTime } from "luxon";
import ReactDatePicker from "react-datepicker";
import Spinner from "react-bootstrap/Spinner";
import { useGoogleSheetsAuthContext } from "../hooks/useGoogleSheetAuthContext";
import {
  checkHistoryMembershipValidity,
  dailyTrackerMemberUsageTable_RemarkLogic,
} from "../util/daily_tracker_remark_logic_util";

export const DailyTrackerMemberUsagePage = () => {
  const { requestToken } = useGoogleSheetsAuthContext();
  const { readSheet } = useGoogleSheetApi({
    spreadsheetId: "1QvN--vhBxs68kwwCCP7diE6MgtRi8yCjOpqAls0OaxA",
  });
  const [error, setError] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);

  const [selectDate, setSelectDate] = React.useState("");

  const [dailyData, setDailyData] = React.useState<
    ReturnType<typeof googleapi_dailyTrackerDataToJson>
  >([]);
  const [dailyDataLoaded, setDailyDataLoaded] = React.useState(false);

  const [transactionData, setTransactionData] = React.useState<
    IReturnGetTransaction[]
  >([]);
  const [transactionDataLoaded, setTransactionDataLoaded] =
    React.useState(false);

  const [tableData, setTableData] = React.useState<
    IDailyTrackerMemberUsageTable[]
  >([]);

  React.useEffect(() => {
    if (!selectDate) return;
    setDailyDataLoaded(false);
    readSheet(`${selectDate}!B2:S200`)
      .then((d) => {
        // console.log(googleapi_dailyTrackerDataToJson(d));
        setDailyData(
          googleapi_dailyTrackerDataToJson(
            d,
            DateTime.fromFormat(selectDate, "MM/dd/yyyy").toJSDate()
          )
        );
        setError("");
        setDailyDataLoaded(true);
      })
      .catch(async (e) => {
        if (e.response.status == 401) {
          requestToken("");
        }
        setError(
          e?.response?.data?.error?.message ||
            e?.message ||
            "Failed to load Google Sheet"
        );
      });
  }, [readSheet, selectDate]);

  React.useEffect(() => {
    if (!selectDate) return;
    setTransactionDataLoaded(false);
    carwashApi_getTransactions({
      transaction_date_min: DateTime.fromFormat(selectDate, "MM/dd/yyyy")
        .set({ hour: 0, minute: 0, second: 0 })
        .toFormat("yyyy-MM-dd'T'HH:mm:ss"),
      transaction_date_max: DateTime.fromFormat(selectDate, "MM/dd/yyyy")
        .set({ hour: 23, minute: 59, second: 59 })
        .toFormat("yyyy-MM-dd'T'HH:mm:ss"),
    })
      .then((r) => {
        setTransactionData(r.data.data);
        setTransactionDataLoaded(true);
      })
      .catch();
  }, [selectDate]);

  React.useEffect(() => {
    if (isLoading) {
      setTableData([]);
    }
  }, [isLoading]);

  React.useEffect(() => {
    if (!transactionDataLoaded || !dailyDataLoaded) return;
    setIsLoading(true);
    pMap(
      dailyData,
      async (d) => {
        const resMembership = await carwashApi_getMembership({
          "cars.plate_number": d.plate_number,
          select: ["cars", "membership_info_kartus"],
        });
        // resMembership.data.data[0].expire_date;

        const foundMemberNumberObj =
          resMembership.data.data?.[0]?.membership_info_kartus?.find(
            (m) => m.nomor_kartu === d.member_number
          );

        const memberWasValid = resMembership.data.data?.[0]
          ? await checkHistoryMembershipValidity(
              resMembership.data.data[0],
              d.member_number,
              d.date
            )
          : false;

        return {
          time: d.time,
          plateNumber: d.plate_number,
          memberNumber: foundMemberNumberObj?.nomor_kartu || "",
          dailyTrackerNewMemberNumber: d.new_member_number,
          dailyTrackerAction: d.action,
          dailyTrackerMemberNumber: d.member_number,
          dailyTrackerMemberStatus: d.member_status,
          dailyTrackerNewExtendMemberDetail: d.new_extend_member_detail,
          isUsed: !!transactionData.find((t) => {
            if (!t?.car) return false;
            return t.car.plate_number === d.plate_number;
          }),
          memberActive: resMembership.data.data?.[0]?.status === "active",
          memberWasValid,
          remark: "",
        } as IDailyTrackerMemberUsageTable;
      },
      { concurrency: 20 }
    ).then((d) => {
      setIsLoading(false);
      setTableData(d.map(dailyTrackerMemberUsageTable_RemarkLogic));
    });
  }, [dailyData, transactionData, transactionDataLoaded, dailyDataLoaded]);

  return (
    <Container>
      <Row>
        <ReactDatePicker
          placeholderText="selectDate"
          dateFormat={"MM/dd/yyyy"}
          onChange={(date) => {
            // field.onChange(date);
            if (date != null)
              setSelectDate(DateTime.fromJSDate(date).toFormat("MM/dd/yyyy"));
          }}
          selected={
            selectDate
              ? DateTime.fromFormat(selectDate, "MM/dd/yyyy").toJSDate()
              : null
          }
        />
      </Row>
      <Row style={{ paddingTop: "10px" }}>
        {isLoading ? (
          <Spinner animation="border" role="status" variant="primary"></Spinner>
        ) : (
          <DailyTrackerMemberUsageTable
            data={tableData}
          ></DailyTrackerMemberUsageTable>
        )}
      </Row>
      {error ? <>{error}</> : <></>}
    </Container>
  );
};
